import { env, flags } from '@/app/config'
import { supabase, unwrap } from '@/services/supabase/client'
import { isIOS, isStandalone } from '@/features/onboarding/install'

export type PushState =
  | 'unsupported'
  | 'needs-install'
  | 'default'
  | 'granted'
  | 'denied'
  | 'disabled'

/**
 * Web Push, the standard kind. No Apple developer account is involved: iOS has
 * supported this since 16.4, but only for a PWA that has been added to the home
 * screen — so on iPhone the app must be installed before it can even ask. (§71, §72)
 */
export function pushSupport(): PushState {
  if (!flags.push || !env.vapidPublicKey) return 'disabled'
  if (typeof window === 'undefined') return 'unsupported'
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return isIOS() && !isStandalone() ? 'needs-install' : 'unsupported'
  }
  if (isIOS() && !isStandalone()) return 'needs-install'
  if (Notification.permission === 'granted') return 'granted'
  if (Notification.permission === 'denied') return 'denied'
  return 'default'
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalised)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

export async function enablePush(): Promise<PushState> {
  const state = pushSupport()
  if (state !== 'default' && state !== 'granted') return state

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'default'

  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(env.vapidPublicKey as string) as BufferSource,
    }))

  const json = subscription.toJSON()
  unwrap(
    await supabase.rpc('register_push_subscription', {
      p_endpoint: subscription.endpoint,
      p_p256dh: json.keys?.p256dh ?? '',
      p_auth: json.keys?.auth ?? '',
      p_user_agent: navigator.userAgent.slice(0, 200),
    }),
  )

  return 'granted'
}

export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  try {
    await supabase.rpc('unregister_push_subscription', { p_endpoint: subscription.endpoint })
  } catch {
    // The local subscription still goes; a stale row is pruned server-side.
  }
  await subscription.unsubscribe()
}

export async function isPushActive(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || Notification.permission !== 'granted') return false
  const registration = await navigator.serviceWorker.ready
  return Boolean(await registration.pushManager.getSubscription())
}
